/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useMemo } from 'react';
import { translate } from '@utils/i18n';
import { InlineSelect } from '@ui/molecules/inputs/inline/InlineSelect';
import CrudHeader from '@components/v2/CrudHeader';
import TabView from '@components/TabView';
import { Button } from '@ui';
import Tags from '@components/Tags';
import ActivityFeed from '@features/ActivityFeed';
import PhotoGallery from '@features/PhotoGallery';
import Loader, { LoaderSizes } from '@components/v2/Loader';
import ActionButtons from './components/ActionButtons';
import { namespace } from './constants';
import DetailInfo from './components/DetailInfo/DetailInfo';
import DeleteModal from './components/DeleteModal';
import ResolveModal from '../components/ResolveModal/ResolveModal';
import useDeficiencyDetail from '../hooks/useDeficiencyDetail';
import useGetResolutionStatus from '../hooks/useGetResolutionStatus';
import useDeficiencyActions from './hooks/useDeficiencyActions';
function DeficiencyDetail({ deficiencyId, inspectionId, deficiencyStatuses, tags }) {
    var _a, _b, _c, _d;
    const { data: deficiency, isLoading, error } = useDeficiencyDetail(deficiencyId);
    const { options: resolutionStatuses } = useGetResolutionStatus();
    const [resolveDialogVisible, setResolveDialogVisible] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const toggleResolveDialog = useCallback(() => setResolveDialogVisible((prev) => !prev), []);
    const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);
    const { updateDeficiency, syncWithAnswer, handleReopen, handleSyncWithAnswer, handleDeficiencyStatusChange, handleResolutionStatusChange, handleTagsChange } = useDeficiencyActions(deficiencyId, namespace);
    const tagsForSelect = useMemo(() => tags.map(({ label, value }) => ({
        label,
        value: Number(value)
    })), [tags]);
    const tabs = [
        {
            id: 'details',
            label: translate('tabs.details', { namespace }),
            content: React.createElement(DetailInfo, { id: deficiencyId })
        },
        {
            id: 'activity',
            label: translate('tabs.activity', { namespace }),
            content: (React.createElement(ActivityFeed, { notesEnabled: false, buildingId: (_a = deficiency === null || deficiency === void 0 ? void 0 : deficiency.building.id) !== null && _a !== void 0 ? _a : 0, deficiencyId: Number(deficiencyId) }))
        },
        {
            id: 'photos',
            label: translate('tabs.photos', { namespace }),
            content: React.createElement(PhotoGallery, { deficiencyId: Number(deficiencyId), uploadInspectionId: inspectionId !== null && inspectionId !== void 0 ? inspectionId : undefined })
        }
    ];
    if (isLoading) {
        return (React.createElement("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }, className: "msgContainer" },
            React.createElement(Loader, { size: LoaderSizes.xlarge })));
    }
    if (error || !deficiency) {
        return React.createElement("div", { className: "msgContainer" }, translate('def_error_message', { namespace }));
    }
    return (React.createElement("div", { className: "main-content" },
        React.createElement("div", { className: "page-content" },
            React.createElement(CrudHeader, { title: `${translate('title', { namespace })} #${deficiency.id}` },
                React.createElement(ActionButtons, null,
                    React.createElement(Button, { onClick: toggleDeleteDialog },
                        React.createElement("i", { className: "fa fa-trash" }),
                        " ",
                        translate('delete', { namespace: 'generic' })),
                    React.createElement(Button, { variant: "secondary", onClick: () => {
                            void handleSyncWithAnswer();
                        }, disabled: syncWithAnswer.isLoading },
                        React.createElement("i", { className: "fa fa-rotate" }),
                        " ",
                        translate('sync_with_answer', { namespace })),
                    deficiency.isResolved ? (React.createElement(Button, { variant: "secondary", onClick: () => {
                            void handleReopen();
                        } },
                        React.createElement("i", { className: "fa fa-rotate-left" }),
                        " ",
                        translate('reopen', { namespace }))) : (React.createElement(Button, { variant: "secondary", onClick: toggleResolveDialog },
                        React.createElement("i", { className: "fa fa-check" }),
                        " ",
                        translate('resolve', { namespace }))))),
            React.createElement("div", { className: "row-fluid d-flex", style: { gap: '1.6rem' } },
                React.createElement("div", { className: "form-group" },
                    React.createElement(InlineSelect, { options: [...deficiencyStatuses].sort((a, b) => a.label.localeCompare(b.label)), value: (_b = deficiency.deficiencyStatus) !== null && _b !== void 0 ? _b : undefined, onChange: (value) => {
                            void handleDeficiencyStatusChange(value);
                        }, label: translate('deficiency_status', { namespace }) })),
                React.createElement("div", { className: "form-group" },
                    React.createElement(InlineSelect, { options: resolutionStatuses.map((status) => ({
                            label: status.label,
                            value: status.value.toString()
                        })), value: (_d = (_c = deficiency.deficiencyResolutionStatus) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : undefined, onChange: (value) => {
                            void handleResolutionStatusChange(value);
                        }, label: translate('resolution_status', { namespace }) })),
                React.createElement("div", { className: "form-group" },
                    React.createElement(Tags, { tagsList: deficiency.tagList, attributeName: "tag_list", tagsForSelect: tagsForSelect, onTagsChange: (values) => {
                            void handleTagsChange(values);
                        } }))),
            React.createElement(TabView, { tabs: tabs }),
            deleteDialogVisible && React.createElement(DeleteModal, { deficiencyId: deficiency.id, toggleDialog: toggleDeleteDialog }),
            resolveDialogVisible && React.createElement(ResolveModal, { toggleDialog: toggleResolveDialog, deficiencyId: deficiency.id }))));
}
export default DeficiencyDetail;
